import { configure, extend } from 'vee-validate'
import { confirmed, double, email, integer, length, max, max_value, min, min_value, oneOf, required } from 'vee-validate/dist/rules'
import { i18n } from './i18n'

configure({
  defaultMessage: (field, values) => {
    // override the field name.
    values._field_ = i18n.t(`fields.${field}`)

    return i18n.t(`validation.${values._rule_}`, values)
  },
})

// Install rules used
extend('min_value', {
  ...min_value,
  message: (_, values) => i18n.t('validation.min_value', values),
})

extend('max_value', {
  ...max_value,
  message: (_, values) => i18n.t('validation.max_value', values),
})

extend('max', {
  ...max,
  message: (_, values) => i18n.t('validation.max', values),
})

extend('required', {
  ...required,
  message: (_, values) => i18n.t('validation.required', values),
})

extend('email', {
  ...email,
  message: (_, values) => i18n.t('validation.email', values),
})

extend('confirmed', {
  ...confirmed,
  message: (_, values) => i18n.t('validation.confirmed', values),
})

extend('min', {
  ...min,
  message: (_, values) => i18n.t('validation.min', values),
})

extend('length', {
  ...length,
  message: (_, values) => i18n.t('validation.length', values),
})

extend('integer', {
  ...integer,
  message: (_, values) => i18n.t('validation.integer', values),
})

extend('double', {
  ...double,
  message: (_, values) => i18n.t('validation.double', values),
})

extend('emailList', {
  validate(value) {
    if (!Array.isArray(value)) return false
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return value.every((email) => emailRegex.test(email))
  },
  message: () => i18n.t('customValidation.emailList'),
})

extend('no_invalid_chars', {
  validate: (value) => !/["<>/\\'{}()\s]/.test(value),
  message: () => i18n.t('customValidation.invalidCharMessage'),
})

/**
 * Validates if a value is a valid URL
 * @param {string} value - The value to validate
 * @returns {boolean|string} True if valid, error message otherwise
 */
extend('url', {
  validate(value) {
    // Allow empty values (use 'required' rule separately if needed)
    if (!value || value === '') {
      return true
    }

    // Reject any whitespace: the URL constructor is lenient and silently
    // encodes spaces (e.g. "http://x.com/a b" -> "http://x.com/a%20b"),
    // which lets garbage past the check. A valid URL contains no whitespace.
    if (/\s/.test(value)) {
      return false
    }

    // Check if it's a valid URL
    try {
      const url = new URL(value)
      // Optionally restrict to http/https protocols
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  },
  message: (fieldName) => i18n.t('customValidation.invalidUrl', { field: fieldName }),
})

extend('one_of', oneOf)

/**
 * Validates a label name - matches API labelsValidator allowed characters
 * Allowed: any Unicode letter (\p{L}: latin, accented, CJK, etc.), any Unicode number (\p{N}: fullwidth digits, etc.), spaces, hyphens, underscores,
 * periods, slashes, ampersands, hashtags, parentheses, brackets
 * Must contain at least one letter or number
 */
extend('label_name', {
  validate(value) {
    if (!value) return true
    const trimmed = value.trim()
    if (trimmed.length === 0) return true

    const hasAlphanumeric = /[\p{L}\p{N}]/u
    const allowedChars = /^[\p{L}\p{N}\s\-_./&#()[\]]+$/u

    if (!hasAlphanumeric.test(trimmed)) {
      return i18n.t('customValidation.labelNameNoAlphanumeric')
    }
    if (!allowedChars.test(trimmed)) {
      return i18n.t('customValidation.labelNameInvalidChars')
    }
    return true
  },
  message: () => i18n.t('customValidation.labelNameInvalidChars'),
})

/**
 * Validates an alphanumeric identifier - no spaces, accents, or special characters
 * Allowed: non-accented letters (A-Z, a-z), numbers (0-9), hyphens, underscores, periods
 */
extend('identifier', {
  validate(value) {
    if (!value) return true
    return /^[A-Za-z0-9\-_.]+$/.test(value)
  },
  message: () => i18n.t('customValidation.identifierInvalid'),
})

/**
 * Validates the technical key of a document custom field, the identity of an entry in the tenant
 * registry. Stricter than `identifier`, which allows the period the document settings api refuses.
 * The message is the one the screen already carries, rather than a second wording of the same rule.
 */
extend('document_field_key', {
  validate(value) {
    if (!value) return true
    return /^[A-Za-z0-9_-]+$/.test(value)
  },
  message: () => i18n.t('documentSettings.invalidKey'),
})

/**
 * Validates if a value is a valid image URL with allowed extensions
 * @param {string} value - The value to validate
 * @param {string[]} extensions - Allowed image extensions (default: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'])
 * @returns {boolean} True if valid, false otherwise
 */
extend('image_url', {
  validate(value, { extensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'] } = {}) {
    // Allow empty values (use 'required' rule separately if needed)
    if (!value || value === '') {
      return true
    }

    // Check if it's a valid URL
    try {
      const url = new URL(value)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return false
      }

      // Extract the pathname and remove query parameters
      const pathname = url.pathname.split('?')[0].toLowerCase()

      // Get file extension
      const extension = pathname.split('.').pop()

      // Check if the extension is in the allowed list
      const normalizedExtensions = extensions.map((ext) => ext.toLowerCase().replace(/^\./, ''))
      return normalizedExtensions.includes(extension)
    } catch {
      return false
    }
  },
  message: (fieldName, { extensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'] } = {}) => {
    return i18n.t('customValidation.invalidImageUrl', {
      field: fieldName,
      extensions: (extensions || []).join(', '),
    })
  },
  params: ['extensions'],
})
extend('end_after_begin_datetime', {
  validate(value, { begin, end }) {
    let startDate = new Date(begin)
    let endDate = new Date(end)
    if (isNaN(startDate.getTime())) {
      startDate = Date.parse('01/01/1970 ' + begin)
    }
    if (isNaN(endDate.getTime())) {
      endDate = Date.parse('01/01/1970 ' + end)
    }
    return startDate < endDate
  },
  message: () => {
    return i18n.t('customValidation.invalidEndDate')
  },
  params: ['begin', 'end'],
})

extend('not_in_the_past', {
  params: ['pickedDate'],
  validate(value, { pickedDate }) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return new Date(pickedDate ?? value) >= today
  },
  message: () => {
    return i18n.t('customValidation.dateInPast')
  },
})

extend('date_do_not_exist', {
  params: ['existingDates'],
  validate(value, { existingDates }) {
    const pickedDate = new Date(value).getTime()
    const parsedDates = JSON.parse(existingDates)
    return !parsedDates.some((date) => new Date(date).getTime() === pickedDate)
  },
  message: () => {
    return i18n.t('customValidation.dateInArray')
  },
})
